import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { MultiSelect } from "@/components/ui/multi-select";
import type { EvaluationForm } from "@/types/evaluation-form";
import { toUserError } from "@/lib/api-error";

const createGroupSchema = z.object({
  name: z.string().min(3, "O nome deve ter pelo menos 3 caracteres"),
  description: z.string().optional(),
  evaluator_id: z.string().min(1, "Selecione um avaliador"),
  form_id: z.string().min(1, "Selecione um formulário de avaliação"),
  areas: z.array(z.string()).optional(),
});

type CreateGroupFormValues = z.infer<typeof createGroupSchema>;

interface CreateEvaluationGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Evaluator {
  id: string;
  full_name: string | null;
}

export function CreateEvaluationGroupModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateEvaluationGroupModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [evaluators, setEvaluators] = useState<Evaluator[]>([]);

  const form = useForm<CreateGroupFormValues>({
    resolver: zodResolver(createGroupSchema),
    defaultValues: {
      name: "",
      description: "",
      evaluator_id: "",
      form_id: "",
      areas: [],
    },
  });

  // Fetch available evaluation forms
  const { data: evaluationForms = [] } = useQuery({
    queryKey: ["evaluation_forms_active"],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("evaluation_forms")
        .select("id, name, description")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      return data as Pick<EvaluationForm, 'id' | 'name' | 'description'>[];
    },
    enabled: isOpen,
  });

  // Fetch areas de atuação
  const { data: areasAtuacao = [] } = useQuery({
    queryKey: ["areas_atuacao", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("areas_atuacao")
        .select("id, nome")
        .eq("is_active", true)
        .order("nome");

      if (error) throw error;
      return data;
    },
    enabled: isOpen,
  });

  const areasOptions = areasAtuacao.map((area) => ({
    label: area.nome,
    value: area.nome,
  }));

  useEffect(() => {
    async function fetchEvaluators() {
      try {
        const { data, error } = await supabase
          .from("v_profiles_with_access_profile")
          .select("id, full_name")
          .order("full_name");

        if (error) throw error;

        const mappedEvaluators = data.map((p) => ({
          id: p.id,
          full_name: p.full_name
        }));

        setEvaluators(mappedEvaluators);
      } catch (error) {
        console.error("Erro ao buscar avaliadores:", error);
      }
    }

    if (isOpen) {
      fetchEvaluators();
      form.reset({
        name: "",
        description: "",
        evaluator_id: "",
        form_id: "",
        areas: [],
      });
    }
  }, [isOpen, form]);

  const onSubmit = async (values: CreateGroupFormValues) => {
    if (!user) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("evaluation_groups").insert({
        name: values.name,
        description: values.description || null,
        evaluator_id: values.evaluator_id,
        form_id: parseInt(values.form_id),
        areas: values.areas && values.areas.length > 0 ? values.areas : null,
        created_by: user.id,
      });

      if (error) throw error;

      toast({
        title: "Grupo criado",
        description: "O grupo de avaliação foi criado com sucesso.",
      });
      form.reset();
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao criar grupo:", error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o grupo de avaliação. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Criar Grupo de Avaliação</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Grupo</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ex: Avaliação Design 2024" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Descrição opcional do grupo..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="form_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Formulário de Avaliação</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um formulário" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {evaluationForms.map((form) => (
                        <SelectItem key={form.id} value={form.id.toString()}>
                          {form.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    O formulário define os critérios de avaliação que serão usados.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="areas"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Áreas de Atuação</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={areasOptions}
                      selected={field.value || []}
                      onChange={field.onChange}
                      placeholder="Selecione as áreas..."
                    />
                  </FormControl>
                  <FormDescription>
                    Áreas de atuação dos freelancers que serão avaliados neste grupo.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="evaluator_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Avaliador Responsável</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um avaliador" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {evaluators.map((evaluator) => (
                        <SelectItem key={evaluator.id} value={evaluator.id}>
                          {evaluator.full_name || "Sem nome"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Criando..." : "Criar Grupo"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
